import {
    IClauseInTopicContext,
    ITopicSection,
    removeClauseFromTopic,
} from "@/api/topics";
import { LinkRouter } from "@/components/shared/buttons/LinkRouter";
import { DestructiveDialog } from "@/components/shared/dialogs/destructive-dialog/DestructiveDialog";
import { Delete } from "@mui/icons-material";
import {
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core/useDialogs";

interface IProps {
    topicId: number;
    section: ITopicSection;
}

export const TopicClausesSection = ({ topicId, section }: IProps) => {
    const dialogs = useDialogs();
    const queryClient = useQueryClient();
    const clauses = section.clauses;

    const handleRemoveClause = async (clause: IClauseInTopicContext) => {
        await dialogs.open(DestructiveDialog, {
            subject: "clause",
            itemId: clause.topic_clause_id,
            mutationFn: (topicClauseId: number) =>
                removeClauseFromTopic(topicId, topicClauseId),
            onSucceeded: () => {
                queryClient.invalidateQueries({
                    queryKey: ["admin-topic-details", String(topicId)],
                });
                queryClient.invalidateQueries({
                    queryKey: ["admin-topics"],
                });
            },
        });
    };

    if (clauses.length === 0) {
        return null;
    }

    return (
        <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={600}>
                Clauses ({clauses.length})
            </Typography>
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600, width: 80 }}>
                                ID
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                Title
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                Description
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                Policy Type
                            </TableCell>

                            <TableCell
                                sx={{ fontWeight: 600, width: 60 }}
                                align="center"
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {clauses.map((clause) => (
                            <TableRow key={clause.topic_clause_id}>
                                <TableCell>
                                    <Typography variant="body2">
                                        {clause.clause_id}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <LinkRouter
                                        to={`/admin/clauses/${clause.clause_id}/details`}
                                        target="_blank"
                                    >
                                        <Typography
                                            variant="body2"
                                            fontWeight={500}
                                        >
                                            {clause.title}
                                        </Typography>
                                    </LinkRouter>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            maxWidth: 300,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {clause.description || "-"}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {clause.policy_type?.short_name || "-"}
                                </TableCell>

                                <TableCell align="center">
                                    <Tooltip title="Remove clause">
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleRemoveClause(clause)
                                            }
                                            color="error"
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
};
